import type {
  V1ResolveAccountSensitiveDataRequest,
  V1ResolveAccountSensitiveDataResponse,
} from '@blk/aladdin-graph-client-axios-internal/wealth/portfolio_management/portfolios/v1/account_api';
import type {
  V1ResolveAggregateSensitiveDataRequest,
  V1ResolveAggregateSensitiveDataResponse,
} from '@blk/aladdin-graph-client-axios-internal/wealth/portfolio_management/portfolios/v1/aggregate_api';
import { ClientDataRequest, CustomRequestHeaderUnion, EnrichedInternalIdentifier } from '@blk/aw-bff-typescript-models';
import { AxiosHeaders, AxiosRequestConfig, AxiosResponse, CancelToken, InternalAxiosRequestConfig } from 'axios';
import {
  addSensitiveDataBrandedType,
  generateRequestConfig,
  sanitizeSensitiveDataBrandedType,
} from '../../utils/CloakDataUtils';
import { AladdinWealthServerGlobalParams } from '../AladdinWealthServer/Constants';
import { resolveAccountSensitiveData, resolveAggregateSensitiveData } from '../AWPortfolioManagement';
import { awSensitiveDataApi } from './Base';
import { generateSensitiveDataFetchRequestPayload } from './utils';

const ACCOUNT_ID_TAG = 'accountID';

export interface PostCloakDataParams extends AladdinWealthServerGlobalParams {
  benchType?: string;
  isStrictMode: boolean;
  pkg: string;
}

const _transformLinkedEntities = (
  linkedEntities?: Array<{
    linkedEntityReference?: { id: string; tag: string };
    sensitiveFields: { [key: string]: string };
  }>
): NonNullable<EnrichedInternalIdentifier['linkedEntities']> =>
  (linkedEntities ?? [])
    .filter(
      (
        linkedEntity
      ): linkedEntity is {
        linkedEntityReference: { id: string; tag: string };
        sensitiveFields: { [key: string]: string };
      } => linkedEntity.linkedEntityReference != null
    )
    .map((linkedEntity) => ({
      id: linkedEntity.linkedEntityReference.id,
      tag: linkedEntity.linkedEntityReference.tag,
      metadata: addSensitiveDataBrandedType(linkedEntity.sensitiveFields),
    }));

const _transformAccountSensitiveData = (data: V1ResolveAccountSensitiveDataResponse): EnrichedInternalIdentifier[] =>
  data.accountSensitiveFields.map((field) => ({
    tag: ACCOUNT_ID_TAG,
    value: field.accountId,
    metadata: addSensitiveDataBrandedType(field.sensitiveFields),
    linkedEntities: _transformLinkedEntities(field.linkedEntities),
  }));

const _transformAggregateSensitiveData = (
  data: V1ResolveAggregateSensitiveDataResponse
): EnrichedInternalIdentifier[] =>
  data.aggregateSensitiveFields
    .filter(
      (field): field is typeof field & { aggregateReference: NonNullable<typeof field.aggregateReference> } =>
        field.aggregateReference != null
    )
    .map((field) => ({
      tag: field.aggregateReference.tag,
      value: field.aggregateReference.id,
      metadata: addSensitiveDataBrandedType(field.sensitiveFields),
      linkedEntities: _transformLinkedEntities(field.linkedEntities),
    }));

/* istanbul ignore next */
const _postAccountSensitiveData = async (
  payload: V1ResolveAccountSensitiveDataRequest
): Promise<AxiosResponse<EnrichedInternalIdentifier[]>> => {
  const response = await resolveAccountSensitiveData(payload);
  return {
    ...response,
    data: _transformAccountSensitiveData(response.data),
  };
};

/* istanbul ignore next */
const _postAggregateSensitiveData = async (
  payload: V1ResolveAggregateSensitiveDataRequest
): Promise<AxiosResponse<EnrichedInternalIdentifier[]>> => {
  const response = await resolveAggregateSensitiveData(payload);
  return {
    ...response,
    data: _transformAggregateSensitiveData(response.data),
  };
};

const getMockEnvironmentFromStorage = (): string | null => {
  try {
    const stored = localStorage.getItem('agraphMockEnvironment');
    if (!stored) {
      return null;
    }

    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed === 'string' && ['beta', 'prod', 'qa', 'uat'].includes(parsed)) {
      return parsed;
    }
  } catch {
    // Invalid JSON or storage access error
  }
  return null;
};

type MockBFFRequest =
  | { type: 'account'; payload: { accountIds: string[] }; env: string }
  | { type: 'aggregate'; payload: { aggregateReferences: Array<{ tag: string; id: string }> }; env: string };

/* istanbul ignore next */
const _postMockBFFSensitiveData = async (
  request: MockBFFRequest
): Promise<AxiosResponse<EnrichedInternalIdentifier[]>> => {
  const endpoint = request.type === 'account' ? 'accounts:resolveSensitiveData' : 'aggregates:resolveSensitiveData';
  const basePath = request.env === 'prod' ? '/aw-bff' : `/aw-bff_${request.env}`;
  const url = `${basePath}/mock/api/cloak/v2/${endpoint}`;

  const fetchResponse = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request.payload),
  });

  const responseData: V1ResolveAccountSensitiveDataResponse | V1ResolveAggregateSensitiveDataResponse =
    await fetchResponse.json();

  let enrichedIdentifiers: EnrichedInternalIdentifier[];
  if ('accountSensitiveFields' in responseData) {
    enrichedIdentifiers = _transformAccountSensitiveData(responseData);
  } else {
    enrichedIdentifiers = _transformAggregateSensitiveData(responseData);
  }

  const axiosHeaders = new AxiosHeaders();
  axiosHeaders.set('Content-Type', 'application/json');

  // Convert fetch headers to plain object (handles both real and mocked responses)
  const responseHeaders: Record<string, string> = {};
  if (fetchResponse.headers && typeof fetchResponse.headers.entries === 'function') {
    for (const [key, value] of fetchResponse.headers.entries()) {
      responseHeaders[key] = value;
    }
  }

  return {
    data: enrichedIdentifiers,
    status: fetchResponse.status,
    statusText: fetchResponse.statusText,
    headers: responseHeaders,
    config: {
      url,
      method: 'POST',
      headers: axiosHeaders,
      data: JSON.stringify(request.payload),
    },
  };
};

/* istanbul ignore next */
export const postCloakData = async (
  params: PostCloakDataParams,
  payload: Required<Pick<ClientDataRequest, 'userIdentity'>> & ClientDataRequest,
  url: string,
  customHeaders: CustomRequestHeaderUnion[],
  clientDataResponseParser: ((rawResponse: string) => EnrichedInternalIdentifier[]) | undefined,
  isBLKHostedSensitiveDataV2RouteEnabled?: boolean,
  cancelToken?: CancelToken
): Promise<AxiosResponse<EnrichedInternalIdentifier[]>> => {
  const isStrictMode = params.isStrictMode;
  const finalPayload = generateSensitiveDataFetchRequestPayload(payload, isStrictMode);
  const config: AxiosRequestConfig = generateRequestConfig(params, customHeaders);
  isStrictMode && delete config['params'];

  if (isBLKHostedSensitiveDataV2RouteEnabled) {
    const mockEnvironment = getMockEnvironmentFromStorage();
    const useMockBFF = mockEnvironment !== null;

    const { accountIds, aggregateReferences } = payload.externalIds.reduce<{
      accountIds: string[];
      aggregateReferences: Array<{ tag: string; id: string }>;
    }>(
      (acc, externalId) => {
        if (externalId.tag === ACCOUNT_ID_TAG) {
          acc.accountIds.push(externalId.value);
        } else {
          acc.aggregateReferences.push({ tag: externalId.tag, id: externalId.value });
        }
        return acc;
      },
      { accountIds: [], aggregateReferences: [] }
    );

    const apiCalls: Promise<AxiosResponse<EnrichedInternalIdentifier[]>>[] = [];

    if (accountIds.length > 0) {
      if (useMockBFF && mockEnvironment) {
        apiCalls.push(_postMockBFFSensitiveData({ type: 'account', payload: { accountIds }, env: mockEnvironment }));
      } else {
        apiCalls.push(
          _postAccountSensitiveData({
            accountIds,
            pkg: params.pkg,
            asOfDate: payload.date,
          })
        );
      }
    }

    if (aggregateReferences.length > 0) {
      if (useMockBFF && mockEnvironment) {
        apiCalls.push(
          _postMockBFFSensitiveData({ type: 'aggregate', payload: { aggregateReferences }, env: mockEnvironment })
        );
      } else {
        apiCalls.push(
          _postAggregateSensitiveData({
            aggregateReferences,
            pkg: params.pkg,
            asOfDate: payload.date,
          })
        );
      }
    }

    const responses = await Promise.allSettled(apiCalls);

    const cloakData = responses.reduce<EnrichedInternalIdentifier[]>((total, current) => {
      if (current.status === 'fulfilled') {
        total.push(...current.value.data);
      } else if (current.status === 'rejected') {
        console.warn(current.reason);
      }
      return total;
    }, []);

    const baseResponse = responses.find((r) => r.status === 'fulfilled');
    if (baseResponse) {
      return {
        ...baseResponse.value,
        data: cloakData,
      };
    }

    const rejectedResponse = responses.find((r) => r.status === 'rejected');
    if (rejectedResponse) {
      return {
        data: [],
        headers: {},
        status: 500,
        statusText: rejectedResponse.reason || 'INTERNAL SERVER ERROR',
        config: config as unknown as InternalAxiosRequestConfig,
      } satisfies AxiosResponse<EnrichedInternalIdentifier[]>;
    }
  }

  // It could return a different shape. Refers to AWM-23897
  const response = await awSensitiveDataApi.post<EnrichedInternalIdentifier[]>(url, finalPayload, {
    ...config,
    cancelToken,
  });
  let data = response.data;
  if (clientDataResponseParser) {
    // Casting as "string" because it's not exactly known what the response shape is. Refers to AWM-23897
    data = clientDataResponseParser(data as unknown as string);
  }
  // Adds the branded type string to the metadata object.
  data = data.map((datum) => ({ ...datum, metadata: addSensitiveDataBrandedType(datum.metadata) }));
  return {
    ...response,
    data,
  };
};

export async function patchCloakData(
  params: PostCloakDataParams,
  payload: EnrichedInternalIdentifier,
  url: string,
  customHeaders: CustomRequestHeaderUnion[]
) {
  const config: AxiosRequestConfig = generateRequestConfig(params, customHeaders);
  payload.metadata = sanitizeSensitiveDataBrandedType(payload.metadata);
  // It could return a different shape. Refers to AWM-23897
  const response = await awSensitiveDataApi.patch<EnrichedInternalIdentifier>(url, payload, config);
  // Adds the branded type string to the metadata object.
  response.data.metadata = addSensitiveDataBrandedType(response.data.metadata);
  return response;
}
